/**
 * CostSummaryPage Component
 *
 * Dashboard page displaying cost summary with charts and metrics.
 * Shows total costs, cost breakdowns, trends, and cost per kg.
 *
 * Part of US-P7-001 Step 5: Cost Summary Dashboard
 */

import { useState, useMemo, useCallback } from 'react'
import { PageLayout } from '@/components/layout'
import { Card, CardContent } from '@/components/ui/card'
import { useCostSummary } from '@/hooks/useCostSummary'
import PeriodSelector, { PERIOD_TYPES, calculatePeriod } from '@/components/costs/PeriodSelector'
import CostSummaryCard from '@/components/costs/CostSummaryCard'
import CostPerKgCard from '@/components/costs/CostPerKgCard'
import CostBreakdownChart from '@/components/costs/CostBreakdownChart'
import CostTrendChart from '@/components/costs/CostTrendChart'

/**
 * Format currency for display
 *
 * @param {number|null} value - Value to format
 * @returns {string} Formatted currency string
 */
function formatCurrency(value) {
  if (value === null || value === undefined) return '£0'
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

/**
 * Format percentage
 *
 * @param {number|null} value - Value to format
 * @returns {string} Formatted percentage string
 */
function formatPercentage(value) {
  if (value === null || value === undefined) return '0%'
  return `${value.toFixed(1)}%`
}

/**
 * CostSummaryPage Component
 *
 * @param {Object} props
 * @param {Object} props.user - Current user object
 * @param {Function} props.onLogout - Logout handler
 */
export default function CostSummaryPage({ user, onLogout }) {
  // Period state
  const [periodType, setPeriodType] = useState(PERIOD_TYPES.MONTH)
  const [periodData, setPeriodData] = useState(() => calculatePeriod(PERIOD_TYPES.MONTH))

  // Trend months: Month/Quarter show 12 months for context, YTD/Trailing scope to period
  const trendMonths = useMemo(() => {
    if (periodType === PERIOD_TYPES.MONTH || periodType === PERIOD_TYPES.QUARTER) {
      return 12
    }
    const start = new Date(periodData.start)
    const end = new Date(periodData.end)
    return (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1
  }, [periodType, periodData.start, periodData.end])

  // Fetch cost summary for selected period
  const {
    summary,
    trend,
    costPerKg,
    grossMargin,
    grossMarginPercent,
    isLoading,
    error
  } = useCostSummary(periodData.start, periodData.end, {
    trendMonths
  })

  // Merge fixed + variable breakdowns for the category chart
  // Filter out metadata fields (source, total, per_kg fields) — only keep actual cost categories
  const mergedBreakdown = useMemo(() => {
    if (!summary) return null
    const EXCLUDE_KEYS = new Set(['total', 'source', 'shiitake_cost_per_kg'])
    const merged = {}
    for (const [key, val] of Object.entries(summary.fixed_costs?.breakdown || {})) {
      if (!EXCLUDE_KEYS.has(key) && Number(val) > 0) {
        merged[key] = (merged[key] || 0) + Number(val)
      }
    }
    for (const [key, val] of Object.entries(summary.variable_costs?.breakdown || {})) {
      if (!EXCLUDE_KEYS.has(key) && Number(val) > 0) {
        merged[key] = (merged[key] || 0) + Number(val)
      }
    }
    return merged
  }, [summary])

  const handlePeriodChange = (newPeriodType, newPeriodData) => {
    setPeriodType(newPeriodType)
    setPeriodData(newPeriodData)
  }

  return (
    <PageLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Cost Summary</h1>
            <p className="text-muted-foreground mt-1">
              {periodData.label}
            </p>
          </div>

          {/* Period Selector */}
          <PeriodSelector
            value={periodType}
            onChange={handlePeriodChange}
          />
        </div>

        {/* Error State */}
        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <p className="text-red-800">
                Failed to load cost data: {error}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Summary Cards Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <CostSummaryCard
            title="Fixed Costs"
            value={summary?.fixed_costs?.total}
            isLoading={isLoading}
          />

          <CostSummaryCard
            title="Variable Costs"
            value={summary?.variable_costs?.total}
            isLoading={isLoading}
          />

          <CostSummaryCard
            title="Total Costs"
            value={summary?.total_costs}
            isLoading={isLoading}
          />

          <CostSummaryCard
            title="Cost per Kg"
            value={costPerKg}
            isLoading={isLoading}
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Category Breakdown */}
          <CostBreakdownChart
            breakdown={mergedBreakdown}
            isLoading={isLoading}
          />

          {/* Cost Trend */}
          <CostTrendChart
            trendData={trend}
            isLoading={isLoading}
          />
        </div>

        {/* Gross Margin Card */}
        {summary?.metrics && (
          <Card className="bg-neutral-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-neutral-600">Gross Margin</p>
                  <p className="text-2xl font-bold text-neutral-900">
                    {formatCurrency(grossMargin)} ({formatPercentage(grossMarginPercent)})
                  </p>
                </div>

                <div className="flex items-center gap-8 text-sm">
                  <div>
                    <p className="text-neutral-600">Revenue</p>
                    <p className="font-medium text-neutral-900">
                      {formatCurrency(summary.revenue)}
                    </p>
                  </div>

                  <div className="border-l pl-8">
                    <p className="text-neutral-600">Production</p>
                    <p className="font-medium text-neutral-900">
                      {(summary.production?.kg_produced || summary.production?.kg_sold)
                        ? `${(summary.production.kg_produced || summary.production.kg_sold).toLocaleString('en-GB')} kg`
                        : '—'}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {!isLoading && !error && !summary && (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-12">
                <p className="text-neutral-500 mb-2">No cost data available for this period</p>
                <p className="text-sm text-neutral-400">
                  Enter fixed costs to see your cost summary
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PageLayout>
  )
}
